import { motion } from "framer-motion";

export default function PaymentStep({
  // ==========================================================
  // ACTUAL ACCOUNT REPORT PLAN
  //
  // snapshot
  // deep_audit
  // ==========================================================

  plan = "snapshot",

  // ==========================================================
  // FEATURE MODE
  //
  // temporary
  // standard
  // ==========================================================

  paymentMode: paymentModeProp,

  onBack,

  // ==========================================================
  // TEMPORARY FLOW
  // ==========================================================

  onPayItForward,
  onViewReport,

  // ==========================================================
  // ONE-TIME FREE REPORT
  // ==========================================================

  freeReportStatus,
  onViewExistingReport,

  // ==========================================================
  // ORIGINAL STANDARD PAID FLOW
  // ==========================================================

  onContinue,
}) {  // ==========================================================
  // PAYMENT MODE
  //
  // IMPORTANT:
  //
  // QuestionnairePage passes this from useCareerFlow.
  //
  // We keep environment/fallback only as protection.
  // ==========================================================

  const paymentMode =
    (
      paymentModeProp ||
      process.env.REACT_APP_REPORT_PAYMENT_MODE ||
      "temporary"
    )
      .toString()
      .trim()
      .toLowerCase();


  // ==========================================================
  // NORMALIZE USER ACCOUNT PLAN
  //
  // IMPORTANT:
  //
  // This component does NOT decide entitlement.
  //
  // useCareerFlow / backend decides:
  //
  // is_premium=false -> snapshot
  // is_premium=true  -> deep_audit
  //
  // PaymentStep simply displays that result.
  // ==========================================================

  const rawPlan =
    (plan || "")
      .toString()
      .trim()
      .toLowerCase();


  let normalizedPlan =
    "snapshot";


  if (
    rawPlan === "deep_audit" ||
    rawPlan === "deep-audit" ||
    rawPlan === "deep audit" ||
    rawPlan === "premium" ||
    rawPlan.includes("deep")
  ) {
    normalizedPlan =
      "deep_audit";
  } else {
    // Anything unknown defaults safely to Snapshot.
    normalizedPlan =
      "snapshot";
  }


  const isDeepAudit =
    normalizedPlan ===
    "deep_audit";


  // ==========================================================
  // REPORT DETAILS
  // ==========================================================

  const reportName =
    isDeepAudit
      ? "Deep Audit Premium Report"
      : "Snapshot Career Report";


  const reportBadge =
    isDeepAudit
      ? "DEEP AUDIT"
      : "SNAPSHOT";


  const standardPrice =
    isDeepAudit
      ? "US$ 19.99"
      : "US$ 9.99";


  const standardDescription =
    isDeepAudit
      ? "Receive a comprehensive career strategy, deeper analysis, reskilling roadmap and detailed action plan."
      : "Get a detailed career analysis with strengths, skill gaps and recommended career directions.";


  // ==========================================================
  // DEBUG
  //
  // Keep while testing.
  // Remove later if needed.
  // ==========================================================

  console.log(
    "[PaymentStep] PAYMENT MODE:",
    paymentMode
  );

  console.log(
    "[PaymentStep] PLAN RECEIVED:",
    plan
  );

  console.log(
    "[PaymentStep] NORMALIZED PLAN:",
    normalizedPlan
  );


  // ==========================================================
  // TEMPORARY FLOW
  //
  // Snapshot account:
  //
  // Snapshot Career Report
  //
  // [Pay It Forward – $5]
  // [View My Report]
  //
  //
  // Deep Audit account:
  //
  // Deep Audit Premium Report
  //
  // [Pay It Forward – $5]
  // [View My Report]
  //
  //
  // IMPORTANT:
  //
  // Both buttons produce the SAME report tier.
  //
  // Only payment choice differs.
  // ==========================================================

  if (
    paymentMode ===
    "temporary"
  ) {
    return (
      <div
        className="
          space-y-8
          text-center
        "
      >

        {/* =====================================================
            HEADER
        ===================================================== */}

        <div>

          <h2
            className="
              text-3xl
              font-bold
              text-pink-400
            "
          >
            Your Career Report Is Ready
          </h2>


          <p
  className="
    text-gray-300
    max-w-2xl
    mx-auto
    mt-4
    leading-relaxed
  "
>
  Your{" "}
  <span
    className="
      text-white
      font-semibold
    "
  >
    {reportName}
  </span>{" "}
  is ready.
</p>

<p
  className="
    text-gray-300
    max-w-2xl
    mx-auto
    mt-4
    leading-relaxed
  "
>
  This report is yours, free of charge.
  If it brings you clarity, consider a $5 contribution
  to help us keep this platform free for everyone
  finding their way.
</p>

        </div>


        {/* =====================================================
            CURRENT ACCOUNT REPORT
        ===================================================== */}

        <div
          className="
            max-w-xl
            mx-auto
            border
            border-pink-500/30
            bg-[#131121]
            rounded-2xl
            p-6
            shadow-lg
          "
        >

          <span
            className="
              inline-block
              text-xs
              font-bold
              text-pink-300
              bg-pink-500/10
              border
              border-pink-500/30
              px-3
              py-1
              rounded-full
            "
          >
            {reportBadge}
          </span>


          <h3
            className="
              text-2xl
              font-bold
              text-white
              mt-4
            "
          >
            {reportName}
          </h3>


          <p
            className="
              text-gray-300
              mt-3
              leading-relaxed
            "
          >
            This is the report included with your
            Reignite.Spirit account.
          </p>


          <p
            className="
              text-sm
              text-gray-400
              mt-3
            "
          >
            Your report type is already determined
            by your account, so you do not need to
            choose another report plan.
          </p>

        </div>


        {/* =====================================================
        {/* =====================================================
    TEMPORARY ACTIONS
===================================================== */}

<div
  className="
    max-w-xl
    mx-auto
    space-y-4
  "
>

  {/* ===================================================
      PAY IT FORWARD - $5
  =================================================== */}

  <motion.button
    type="button"

    whileHover={{
      scale: 1.02,
    }}

    whileTap={{
      scale: 0.98,
    }}

    onClick={async () => {
      console.log(
        "[PaymentStep] PAY IT FORWARD:",
        normalizedPlan
      );

      if (
        typeof onPayItForward ===
        "function"
      ) {
        await onPayItForward();
      }
    }}

    className="
      w-full
      px-8
      py-4
      bg-gradient-to-r
      from-[#EB6AB5]
      to-[#ff83d1]
      rounded-full
      text-[#0F0A24]
      font-bold
      text-lg
      shadow-lg
      hover:shadow-xl
      transition
    "
  >
    Pay It Forward - Help us keep this free and make a difference in others' lives. - $5
  </motion.button>


  {/* ===================================================
      VIEW REPORT
  =================================================== */}

  {freeReportStatus?.freeReportUsed &&
  freeReportStatus?.assessmentId ? (

    // =================================================
    // REPORT ALREADY GENERATED
    //
    // NO GEMINI
    // NO POST /generate
    // =================================================

    <motion.button
      type="button"

      whileHover={{
        scale: 1.02,
      }}

      whileTap={{
        scale: 0.98,
      }}

      onClick={async () => {
        console.log(
          "[PaymentStep] VIEW EXISTING REPORT:",
          freeReportStatus.assessmentId
        );

        if (
          typeof onViewExistingReport ===
          "function"
        ) {
          await onViewExistingReport(
            freeReportStatus.assessmentId
          );
        }
      }}

      className="
        w-full
        px-8
        py-4
        bg-transparent
        border
        border-gray-500
        rounded-full
        text-white
        font-semibold
        text-lg
        hover:bg-white/5
        hover:border-gray-300
        transition
      "
    >
      View Existing Report
    </motion.button>

  ) : (

    // =================================================
    // FIRST FREE REPORT
    //
    // ONLY THIS PATH MAY GENERATE
    // =================================================

    <motion.button
      type="button"

      whileHover={{
        scale: 1.02,
      }}

      whileTap={{
        scale: 0.98,
      }}

      onClick={async () => {
        console.log(
          "[PaymentStep] VIEW MY REPORT:",
          normalizedPlan
        );

        if (
          typeof onViewReport ===
          "function"
        ) {
          await onViewReport();
        }
      }}

      disabled={
        freeReportStatus?.loaded ===
        false
      }

      className="
        w-full
        px-8
        py-4
        bg-transparent
        border
        border-gray-500
        rounded-full
        text-white
        font-semibold
        text-lg
        hover:bg-white/5
        hover:border-gray-300
        transition
        disabled:opacity-50
        disabled:cursor-wait
      "
    >
      {freeReportStatus?.loaded ===
      false
        ? "Checking Report Access..."
        : "View My Report"}
    </motion.button>

  )}

</div>
        {/* =====================================================
            TEMPORARY EXPLANATION
        ===================================================== */}

        <div
          className="
            max-w-xl
            mx-auto
          "
        >

          <p
  className="
    text-sm
    text-gray-400
    leading-relaxed
  "
>
  Your report remains free whether or not
  you choose to contribute.
</p>


          <p
            className="
              text-xs
              text-gray-500
              mt-2
            "
          >
            Your report type is determined by your
            existing Reignite.Spirit account plan.
          </p>

        </div>


        {/* =====================================================
            BACK
        ===================================================== */}

        <button
          type="button"

          onClick={
            onBack
          }

          className="
            block
            mx-auto
            text-sm
            text-gray-400
            hover:text-white
            transition
          "
        >
          ← Back
        </button>

      </div>
    );
  }


  // ==========================================================
  // ORIGINAL STANDARD PAID FLOW
  //
  // IMPORTANT:
  //
  // THIS IS NOT DELETED.
  //
  // It is hidden whenever:
  //
  // REACT_APP_REPORT_PAYMENT_MODE=temporary
  //
  //
  // Later:
  //
  // REACT_APP_REPORT_PAYMENT_MODE=standard
  //
  // restores this flow.
  //
  //
  // Snapshot account:
  //
  // Snapshot $9.99
  //
  //
  // Deep Audit account:
  //
  // Deep Audit $19.99
  //
  //
  // The user does NOT select between both reports.
  // Account plan determines the report.
  // ==========================================================

  return (
    <div
      className="
        space-y-8
        text-center
      "
    >

      {/* =====================================================
          HEADER
      ===================================================== */}

      <div>

        <h2
          className="
            text-3xl
            font-bold
            text-pink-400
          "
        >
          Your Career Report
        </h2>


        <p
          className="
            text-gray-300
            max-w-2xl
            mx-auto
            mt-3
          "
        >
          Continue to unlock your{" "}

          <span
            className="
              text-white
              font-semibold
            "
          >
            {reportName}
          </span>

          .
        </p>

      </div>


      {/* =====================================================
          USER'S ACCOUNT REPORT ONLY
      ===================================================== */}

      <motion.div
        whileHover={{
          y: -5,
        }}

        className="
          max-w-xl
          mx-auto
          border
          border-pink-500/30
          bg-[#131121]
          rounded-2xl
          p-7
          flex
          flex-col
          text-left
          shadow-lg
        "
      >

        {/* BADGE */}

        <span
          className="
            inline-block
            self-start
            text-xs
            font-bold
            text-pink-300
            bg-pink-500/10
            border
            border-pink-500/30
            px-3
            py-1
            rounded-full
          "
        >
          {
            isDeepAudit
              ? "MOST DETAILED"
              : "ENHANCED"
          }
        </span>


        {/* REPORT NAME */}

        <h3
          className="
            text-2xl
            font-bold
            text-white
            mt-5
          "
        >
          {
            isDeepAudit
              ? "Deep Audit Report"
              : "Snapshot Report"
          }
        </h3>


        {/* ORIGINAL PRICE */}

        <p
          className="
            text-3xl
            font-bold
            text-pink-400
            mt-3
          "
        >
          {standardPrice}
        </p>


        {/* DESCRIPTION */}

        <p
          className="
            text-gray-300
            mt-4
            leading-relaxed
          "
        >
          {standardDescription}
        </p>


        {/* ===================================================
            ORIGINAL PAYMENT BUTTON
        =================================================== */}

        <motion.button
          type="button"

          whileHover={{
            scale: 1.02,
          }}

          whileTap={{
            scale: 0.98,
          }}

          onClick={async () => {
            console.log(
              "[PaymentStep] STANDARD PURCHASE:",
              normalizedPlan
            );

            if (
              typeof onContinue ===
              "function"
            ) {
              await onContinue(
                normalizedPlan
              );
            }
          }}

          className="
            mt-7
            w-full
            px-6
            py-4
            bg-gradient-to-r
            from-[#EB6AB5]
            to-[#ff83d1]
            rounded-full
            text-[#0F0A24]
            font-bold
            text-lg
            hover:scale-[1.01]
            transition
            shadow-lg
          "
        >
          {
            isDeepAudit
              ? "Continue with Deep Audit – US$19.99"
              : "Continue with Snapshot – US$9.99"
          }
        </motion.button>

      </motion.div>


      {/* =====================================================
          STANDARD MODE NOTE
      ===================================================== */}

      <p
        className="
          text-sm
          text-gray-400
          max-w-xl
          mx-auto
        "
      >
        Your report type is based on the plan associated
        with your Reignite.Spirit account.
      </p>


      {/* =====================================================
          BACK
      ===================================================== */}

      <button
        type="button"

        onClick={
          onBack
        }

        className="
          block
          mx-auto
          text-sm
          text-gray-400
          hover:text-white
          transition
        "
      >
        ← Back
      </button>

    </div>
  );
}